// Icon set for the placement chrome. Hand-traced approximations of the
// Instagram / Facebook / YouTube / Google glyphs at 24×24 — close enough
// to read as the real UI at preview scale without shipping platform
// assets.
//
// Stroke-based by default so a single `size` scales cleanly; glyphs
// that are solid in the real UI carry their own fill.

import type { CSSProperties } from 'react';
import { safeHttpUrl } from '../../lib/safeHref';

export const ICON_PATHS: Record<string, string> = {
  heart:    '<path d="M12 21C10 19.5 3 14.6 3 9.3 3 6.4 5.3 4 8.1 4c1.6 0 3 .8 3.9 2 .9-1.2 2.3-2 3.9-2C18.7 4 21 6.4 21 9.3c0 5.3-7 10.2-9 11.7z"/>',
  comment:  '<path d="M12 20.5c4.7 0 8.5-3.4 8.5-8S16.7 4.5 12 4.5 3.5 7.9 3.5 12.5c0 1.7.5 3.2 1.4 4.5L4 21l4.1-1.1c1.2.4 2.5.6 3.9.6z"/>',
  send:     '<path d="M21 3L10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5 21 3z"/>',
  bookmark: '<path d="M6 3.5h12V21l-6-4.3L6 21V3.5z"/>',
  dots:     '<g fill="currentColor" stroke="none"><circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/></g>',
  music:    '<path d="M9 17.5V6l11-2v11.5"/><g fill="currentColor" stroke="none"><circle cx="7" cy="17.5" r="2.2"/><circle cx="18" cy="15.5" r="2.2"/></g>',
  x:        '<path d="M5 5l14 14M19 5L5 19"/>',
  chevR:    '<path d="M9 5l7 7-7 7"/>',
  chevU:    '<path d="M5 15l7-7 7 7"/>',
  globe:    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.5 3.8 5.6 3.8 9s-1.3 6.5-3.8 9m0-18C9.5 5.5 8.2 8.6 8.2 12s1.3 6.5 3.8 9"/>',
  thumb:    '<path d="M7 10.5V21H4.5A1.5 1.5 0 0 1 3 19.5v-7.5a1.5 1.5 0 0 1 1.5-1.5H7zm0 0l4-8a2.5 2.5 0 0 1 2.5 2.5V9H19a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 17.8 20H7"/>',
  shareFb:  '<path d="M14 9.5l6.5-5.5v15L14 13.5v3C7.5 16.5 4.5 19 3.5 21.5c0-7 3.5-11.5 10.5-12v-4z"/>',
  camera:   '<rect x="3" y="7" width="18" height="13" rx="3"/><circle cx="12" cy="13.5" r="3.6"/><path d="M8.5 7l1.4-2.5h4.2L15.5 7"/>',
  // The Stories link sticker's chain glyph — two interlocking capsule
  // links on the diagonal, which is what makes it read as "that weird
  // link emoji" rather than a conventional hyperlink.
  link:     '<path d="M10.1 13.9a3.6 3.6 0 0 0 5.1 0l2.6-2.6a3.6 3.6 0 0 0-5.1-5.1l-1.3 1.3"/><path d="M13.9 10.1a3.6 3.6 0 0 0-5.1 0l-2.6 2.6a3.6 3.6 0 0 0 5.1 5.1l1.3-1.3"/>',

  // ── Google / YouTube ──
  search:   '<circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5L21 21"/>',
  // YouTube's share is an outline arrow rather than Facebook's solid one.
  shareYt:  '<path d="M3 20c0-6.5 4.5-10 10-10V6l8 6-8 6v-4c-4.5 0-7.5 1.8-10 6z"/>',
  // Skip-ad glyph: double chevron into a bar.
  skip:     '<path d="M5 6l6 6-6 6M12 6l6 6-6 6M20 6v12"/>',
  // AdChoices: the blue "i" roundel plus the little triangle. This is
  // the marker Google composites onto display/native creative itself,
  // so it belongs on the media, not in the surrounding card.
  adchoices: '<circle cx="9" cy="9" r="7"/><path d="M9 6.2v.2M9 9v3.6"/><path d="M15.5 15.5H22L18.75 21z" fill="currentColor" stroke="none"/>',
  play:     '<path d="M7 4.5l13 7.5-13 7.5z" fill="currentColor" stroke="none"/>',
  volume:   '<path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z"/><path d="M16 9a4.5 4.5 0 0 1 0 6"/>',
  gear:     '<circle cx="12" cy="12" r="3"/><path d="M12 2.8v2.6M12 18.6v2.6M4.5 7.5l2.3 1.3M17.2 15.2l2.3 1.3M4.5 16.5l2.3-1.3M17.2 8.8l2.3-1.3"/>',
  expand:   '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>'
};

export function Icon({
  name, size, shadow, style
}: {
  name:    string;
  size:    number;
  shadow?: boolean;
  style?:  CSSProperties;
}) {
  const d = ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
      style={{
        display: 'block', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
        strokeLinecap: 'round', strokeLinejoin: 'round', flex: 'none',
        filter: shadow ? 'drop-shadow(0 1px 2px rgba(0,0,0,.5))' : undefined,
        ...style
      }}
      dangerouslySetInnerHTML={{ __html: d }}
    />
  );
}

export function Avatar({
  logoUrl, name, size, ring
}: {
  logoUrl?: string | null;
  name:     string;
  size:     number;
  // Stories draws the creator chip inside a gradient ring; Feed doesn't.
  ring?:    boolean;
}) {
  const logoSrc = safeHttpUrl(logoUrl);
  const inner = (
    <div style={{
      width: '100%', height: '100%', borderRadius: '50%', background: '#dfe3ea', color: '#333',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.46, overflow: 'hidden'
    }}>
      {logoSrc
        ? <img src={logoSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : (name || 'B').slice(0, 1).toUpperCase()}
    </div>
  );

  if (!ring) {
    return <div style={{ width: size, height: size, flex: 'none' }}>{inner}</div>;
  }
  return (
    <div style={{
      width: size, height: size, flex: 'none', borderRadius: '50%',
      padding: Math.max(1, size * 0.06),
      background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)',
      boxSizing: 'border-box'
    }}>
      <div style={{ width: '100%', height: '100%', borderRadius: '50%', padding: Math.max(1, size * 0.04), background: '#000', boxSizing: 'border-box' }}>
        {inner}
      </div>
    </div>
  );
}
